import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getPaddleEnvironment } from '@/lib/paddle';
import { track } from '@/lib/telemetry';

type PushState = {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
  loading: boolean;
  iosNeedsPwa: boolean;
};

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function detectIosNeedsPwa(): boolean {
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  if (!isIos) return false;
  const isStandalone = (window.navigator as any).standalone === true
    || window.matchMedia('(display-mode: standalone)').matches;
  return !isStandalone;
}

export function usePushSubscription() {
  const { mode, userId, dealershipId } = useAuth();
  const [state, setState] = useState<PushState>({
    supported: false,
    permission: 'unsupported',
    subscribed: false,
    loading: true,
    iosNeedsPwa: false,
  });

  const refresh = useCallback(async () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    if (!supported) {
      setState({ supported: false, permission: 'unsupported', subscribed: false, loading: false, iosNeedsPwa: detectIosNeedsPwa() });
      return;
    }
    let subscribed = false;
    try {
      const reg = await navigator.serviceWorker.getRegistration('/push-sw.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        subscribed = !!sub;
      }
    } catch { /* noop */ }
    setState({
      supported: true,
      permission: Notification.permission,
      subscribed,
      loading: false,
      iosNeedsPwa: detectIosNeedsPwa(),
    });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const subscribe = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (mode !== 'real' || !userId || !dealershipId) return { ok: false, error: 'Sign in first' };
    if (!('serviceWorker' in navigator)) return { ok: false, error: 'Push not supported on this browser' };

    // Subscription gate — push notifications are paid.
    try {
      const { data: sub } = await supabase
        .from('subscriptions')
        .select('status,current_period_end')
        .eq('user_id', userId)
        .eq('environment', getPaddleEnvironment())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const endOk = !sub?.current_period_end || new Date(sub.current_period_end) > new Date();
      const active = sub && (
        ((sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due') && endOk)
        || (sub.status === 'canceled' && !!sub.current_period_end && new Date(sub.current_period_end) > new Date())
      );
      if (!active) {
        return { ok: false, error: 'Push notifications require an active plan. Start a free trial from Pricing.' };
      }
    } catch { /* if check fails, fall through and let normal flow proceed */ }

    try {
      // Permission
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return { ok: false, error: 'Notification permission denied' };

      // Register SW (idempotent)
      const reg = await navigator.serviceWorker.register('/push-sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const { data: keyResp, error: keyErr } = await supabase.functions.invoke('get-vapid-public-key');
      if (keyErr || !keyResp?.key) return { ok: false, error: 'Server missing VAPID key' };

      // Subscribe
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const keyBytes = urlBase64ToUint8Array(keyResp.key);
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: keyBytes.buffer.slice(keyBytes.byteOffset, keyBytes.byteOffset + keyBytes.byteLength) as ArrayBuffer,
        });
      }
      const json = sub.toJSON();
      const p256dh = json.keys?.p256dh;
      const auth = json.keys?.auth;
      if (!sub.endpoint || !p256dh || !auth) return { ok: false, error: 'Bad subscription' };

      const { error: insErr } = await supabase
        .from('push_subscriptions')
        .upsert(
          [{
            user_id: userId,
            dealership_id: dealershipId,
            endpoint: sub.endpoint,
            p256dh,
            auth,
            user_agent: navigator.userAgent.slice(0, 200),
            last_seen_at: new Date().toISOString(),
          }],
          { onConflict: 'user_id,endpoint' },
        );
      if (insErr) return { ok: false, error: insErr.message };

      // Mark onboarding step
      await supabase
        .from('user_onboarding')
        .upsert([{ user_id: userId, enabled_notifications_at: new Date().toISOString() }], { onConflict: 'user_id' });

      await refresh();
      track('push_enabled', { userId, dealershipId, mode }, {});
      return { ok: true };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  }, [mode, userId, dealershipId, refresh]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/push-sw.js');
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint);
        await sub.unsubscribe();
      }
      await refresh();
    } catch { /* noop */ }
  }, [refresh]);

  return { ...state, subscribe, unsubscribe, refresh };
}
